"use client"

import {
  BookOpen,
  CheckCircle,
  FileText,
  Home,
  Mail,
  PlayCircle,
  PlusIcon,
  Search,
  Settings,
  Users,
  X,
} from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Logo } from "@/components/logo"
import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { useDirectoryAdmin } from "@/hooks/use-directory-admin"
import { useUserType } from "@/hooks/use-user-type"
import { trpc } from "@/lib/trpc"
import { InviteCollaboratorModal } from "./modals/invite-collaborator"
import { ProfessionalTutorialVideoModal } from "./modals/professional-tutorial-video-modal"
import { Button } from "./ui/button"

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation()
  const userQuery = trpc.users.me.useQuery()
  const user = userQuery.data
  const { isProfessional } = useUserType()
  const { isAdmin: isDirectoryAdmin } = useDirectoryAdmin()
  const { setOpenMobile } = useSidebar()

  const userObject = {
    name: `${user?.firstName} ${user?.lastName}`,
    email: user?.email ?? "",
  }

  const professionalNavMain = [
    {
      title: t("components.sidebar.professional.allClients"),
      url: "/professional/clients",
      icon: Users,
    },
    {
      title: "Invitations",
      url: "/professional/invitations",
      icon: Mail,
    },
    {
      title: t("components.sidebar.servicesDirectory"),
      url: "/professional/services",
      icon: Search,
    },
  ]

  const individualNavMain = [
    {
      title: t("components.sidebar.individual.dashboard"),
      url: "/personal/dashboard",
      icon: Home,
    },
    {
      title: t("components.sidebar.individual.journalManagement"),
      url: "#",
      icon: BookOpen,
      items: [
        {
          title: t("components.sidebar.individual.myJournals"),
          url: "/personal/journal-management",
        },
        {
          title: "Journal Invitations",
          url: "/personal/journal-invitations",
        },
      ],
    },
    {
      title: t("components.sidebar.individual.goals"),
      url: "/personal/goals",
      icon: CheckCircle,
    },
    {
      title: t("components.sidebar.individual.collaborators"),
      url: "#",
      icon: Users,
      items: [
        {
          title: t("components.sidebar.individual.myCollaborators"),
          url: "/personal/collaborators",
        },
        {
          title: t("components.sidebar.individual.collaboratorRequests"),
          url: "/personal/collaboration-requests",
        },
      ],
    },
    {
      title: t("components.sidebar.individual.guidedForms"),
      url: "/personal/guided-forms",
      icon: FileText,
    },
    {
      title: t("components.sidebar.servicesDirectory"),
      url: "/personal/services",
      icon: Search,
    },
    // {
    //   title: t("components.sidebar.helpCenter"),
    //   url: "#",
    //   icon: LifeBuoy,
    // },
  ]

  const adminNav = isDirectoryAdmin
    ? [
        {
          title: t("components.sidebar.directoryAdmin"),
          url: "/admin/listings",
          icon: Settings,
        },
      ]
    : []

  const navMain = [...(isProfessional ? professionalNavMain : individualNavMain), ...adminNav]
  const [open, setOpen] = useState(false)

  return (
    <Sidebar variant="inset" collapsible="icon" {...props}>
      <SidebarHeader className="relative">
        <Logo className="m-auto my-6" />

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-3 top-3 size-9 md:hidden"
          aria-label="Close navigation"
          onClick={() => setOpenMobile(false)}
        >
          <X className="size-5" aria-hidden="true" />
        </Button>
      </SidebarHeader>

      <SidebarContent>
        <NavMain
          items={navMain}
          prependItem={
            <SidebarMenuItem>
              {isProfessional ? (
                <InviteCollaboratorModal
                  open={open}
                  setOpen={setOpen}
                  triggerSlot={
                    <SidebarMenuButton
                      tooltip={t("components.sidebar.professional.addNewClient")}
                      onClick={() => {
                        setOpen(true)
                      }}
                    >
                      <PlusIcon />
                      <span>{t("components.sidebar.professional.addNewClient")}</span>
                    </SidebarMenuButton>
                  }
                />
              ) : (
                <SidebarMenuButton
                  tooltip={t("components.sidebar.individual.newJournal")}
                  onClick={() => {
                    alert(t("components.sidebar.individual.newJournal"))
                  }}
                >
                  <PlusIcon />
                  <span>{t("components.sidebar.individual.newJournal")}</span>
                </SidebarMenuButton>
              )}
            </SidebarMenuItem>
          }
        />
      </SidebarContent>
      <SidebarFooter>
        {isProfessional && (
          <>
            <ProfessionalTutorialVideoModal
              triggerSlot={
                <Button variant="outline" className="w-full">
                  <PlayCircle className="h-4 w-4" />
                  {t("components.sidebar.tutorial")}
                </Button>
              }
            />
            <Button asChild variant="default" className="w-full">
              <a href="mailto:admin@legacykeeper.app">
                <Mail className="h-4 w-4" />
                {t("components.sidebar.requestSupport")}
              </a>
            </Button>
          </>
        )}
        <NavUser user={userObject} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
